import { useState, useEffect } from 'react';
import ConversationView from '../../components/ConversationView';
import { subscribeToDoubleBagelConversations, broadcastMessage } from '../../services/chat';
import { DOUBLE_BAGEL_UID } from '../../constants';
import type { Conversation } from '../../types/chat';
import { useToast } from '../../hooks/useToast';

export default function AdminSupport() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    const unsubscribe = subscribeToDoubleBagelConversations((convs) => {
      setConversations(convs);
      setLoading(false);
      // Auto-select first conversation on desktop if none selected
      setSelectedConversation(prev => {
        if (!prev && convs.length > 0) return convs[0];
        if (prev) {
          // Keep selection in sync if updated
          const updated = convs.find(c => c.id === prev.id);
          return updated ?? prev;
        }
        return prev;
      });
    });
    return unsubscribe;
  }, []);

  const getOtherParticipant = (conversation: Conversation) => {
    const otherUserId = conversation.participants.find(id => id !== DOUBLE_BAGEL_UID);
    if (!otherUserId) return { name: 'Unknown', avatarUrl: undefined };
    const details = conversation.participantDetails?.[otherUserId];
    return { name: details?.name || 'Unknown', avatarUrl: details?.avatarUrl };
  };

  const formatTime = (timestamp: unknown) => {
    if (!timestamp) return '';
    const date = (timestamp as { toDate?: () => Date }).toDate?.() || new Date(timestamp as string);
    const diffMs = Date.now() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    const hrs = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    const weeks = Math.floor(diffMs / 604800000);
    if (mins < 60) return `${mins}m`;
    if (hrs < 24) return `${hrs}h`;
    if (days < 7) return `${days}d`;
    return `${weeks}w`;
  };

  const filteredConversations = conversations.filter(c => {
    if (!searchQuery.trim()) return true;
    return getOtherParticipant(c).name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleBroadcast = async () => {
    if (!broadcastText.trim()) return;
    setBroadcasting(true);
    try {
      await broadcastMessage(broadcastText.trim());
      showSuccess(`Broadcast sent to ${conversations.length} user${conversations.length !== 1 ? 's' : ''}`);
      setBroadcastText('');
      setShowBroadcast(false);
    } catch (err) {
      showError('Failed to send broadcast');
      console.error(err);
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Left column — conversation list */}
      <div className="w-72 flex-shrink-0 flex flex-col border-r border-gray-200 bg-white">
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Support Inbox</h2>
          <button
            onClick={() => setShowBroadcast(true)}
            title="Broadcast to all users"
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3L9.218 10.083M11.698 20.334L7 11l15-8-6.302 9.334z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b border-gray-100">
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" className="text-gray-400 flex-shrink-0">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2"/>
              <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm text-gray-700 placeholder:text-gray-400 outline-none input-glow"
            />
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
              Loading…
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm px-4">
              {searchQuery ? 'No conversations found' : 'No support conversations yet'}
            </div>
          ) : (
            filteredConversations.map(conversation => {
              const participant = getOtherParticipant(conversation);
              const isSelected = selectedConversation?.id === conversation.id;

              return (
                <div
                  key={conversation.id}
                  onClick={() => setSelectedConversation(conversation)}
                  className={`flex gap-3 items-start px-3 py-3 border-b border-gray-100 cursor-pointer transition-colors ${
                    isSelected ? 'bg-tennis-green/5 border-l-2 border-l-tennis-green' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-[#0A1628] flex items-center justify-center text-white font-semibold text-sm">
                    {participant.avatarUrl ? (
                      <img src={participant.avatarUrl} alt={participant.name} className="w-full h-full object-cover" />
                    ) : (
                      participant.name.charAt(0).toUpperCase()
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 mb-0.5">
                      <span className="text-[13px] font-semibold text-gray-900 truncate">{participant.name}</span>
                      <span className="text-[11px] text-gray-400 flex-shrink-0">
                        {formatTime(conversation.lastMessageAt || conversation.createdAt)}
                      </span>
                    </div>
                    <p className="text-[12px] text-gray-500 line-clamp-1">
                      {conversation.lastMessage || 'No messages yet'}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right column — conversation view */}
      <div className="flex-1 overflow-hidden bg-white">
        {selectedConversation ? (
          <ConversationView
            conversationId={selectedConversation.id}
            senderOverride={DOUBLE_BAGEL_UID}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <div className="text-center">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto mb-3 text-gray-300">
                <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p className="text-sm">Select a conversation</p>
            </div>
          </div>
        )}
      </div>

      {/* Broadcast modal */}
      {showBroadcast && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Broadcast Message</h3>
              <button
                onClick={() => { setShowBroadcast(false); setBroadcastText(''); }}
                className="p-1 rounded text-gray-400 hover:text-gray-600 transition"
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 5L5 15M5 5L15 15" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              This message will be sent to all {conversations.length} user{conversations.length !== 1 ? 's' : ''} as <strong>Double Bagel</strong>.
            </p>

            <textarea
              value={broadcastText}
              onChange={e => setBroadcastText(e.target.value)}
              placeholder="Type your message…"
              rows={4}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 resize-none outline-none focus:ring-2 focus:ring-tennis-green/30 focus:border-tennis-green mb-4 input-glow"
              autoFocus
            />

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowBroadcast(false); setBroadcastText(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleBroadcast}
                disabled={!broadcastText.trim() || broadcasting}
                className="px-4 py-2 text-sm font-medium bg-tennis-green text-white rounded-lg hover:bg-tennis-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition btn-press"
              >
                {broadcasting ? 'Sending…' : 'Send Broadcast'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
